import {
    Phone,
    Mail,
    MapPin,
    Facebook,
    Instagram,
    FileText,
} from "lucide-react";
import { COMPANY } from "../lib/constants";

export default function Contact() {
    return (
        <div data-testid="contact-page">
            <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12">
                <div className="overline mb-4 fade-up">Kontakt</div>
                <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] text-[#2C241B] max-w-4xl fade-up fade-up-delay-1">
                    Tag fat i os —<br />
                    <span className="italic text-[#2F483A]">
                        vi glæder os til at høre fra dig.
                    </span>
                </h1>
                <p className="mt-8 max-w-2xl text-lg text-[#4A3B2C] font-light leading-relaxed fade-up fade-up-delay-2">
                    Vi giver altid et uforpligtende tilbud. Ring direkte til
                    Markus i dagtimerne — tager han den ikke, ringer han
                    tilbage så hurtigt han kan. Eller skriv en mail.
                </p>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
                {/* Primary contact */}
                <div className="md:col-span-7 space-y-4">
                    <a
                        href={COMPANY.phoneHref}
                        data-testid="contact-phone-link"
                        className="group block border-t border-[#2C241B]/20 py-8 md:py-10 hover:bg-[#EAE3D5]/40 transition-colors"
                    >
                        <div className="flex items-start justify-between gap-6">
                            <div>
                                <div className="overline mb-3">
                                    <Phone
                                        size={12}
                                        className="inline mr-2 -mt-0.5"
                                    />
                                    Telefon
                                </div>
                                <div className="font-serif text-5xl md:text-7xl text-[#2C241B] group-hover:text-[#2F483A] transition-colors leading-none">
                                    {COMPANY.phone}
                                </div>
                            </div>
                        </div>
                    </a>

                    <a
                        href={COMPANY.emailHref}
                        data-testid="contact-email-link"
                        className="group block border-t border-[#2C241B]/20 py-8 md:py-10 hover:bg-[#EAE3D5]/40 transition-colors"
                    >
                        <div className="overline mb-3">
                            <Mail
                                size={12}
                                className="inline mr-2 -mt-0.5"
                            />
                            Mail
                        </div>
                        <div className="font-serif text-3xl md:text-5xl text-[#2C241B] group-hover:text-[#2F483A] transition-colors break-all leading-tight">
                            {COMPANY.email}
                        </div>
                    </a>

                    <div className="border-t border-b border-[#2C241B]/20 py-8 md:py-10">
                        <div className="overline mb-3">
                            <MapPin
                                size={12}
                                className="inline mr-2 -mt-0.5"
                            />
                            Adresse
                        </div>
                        <div className="font-serif text-3xl md:text-4xl text-[#2C241B] leading-tight">
                            Stubmarken 28<br />3230 Græsted
                        </div>
                    </div>
                </div>

                {/* Sidebar — firma-info & social */}
                <aside className="md:col-span-5 md:pt-2">
                    <div className="bg-[#2F483A] text-[#F9F6F0] p-8 md:p-10 rounded-sm grain">
                        <div className="overline text-[#F9F6F0]/70 mb-3">
                            <FileText
                                size={12}
                                className="inline mr-2 -mt-0.5"
                            />
                            Firma-info
                        </div>
                        <div className="font-serif text-2xl md:text-3xl mb-6 leading-tight">
                            {COMPANY.name}
                        </div>
                        <dl className="space-y-4 text-sm text-[#F9F6F0]/85">
                            <div className="flex justify-between gap-4">
                                <dt className="text-[#F9F6F0]/55">CVR</dt>
                                <dd>{COMPANY.cvr}</dd>
                            </div>
                            <div className="flex justify-between gap-4">
                                <dt className="text-[#F9F6F0]/55">Ejer</dt>
                                <dd>{COMPANY.owner}</dd>
                            </div>
                            <div className="flex justify-between gap-4">
                                <dt className="text-[#F9F6F0]/55">
                                    Del af
                                </dt>
                                <dd className="text-right">
                                    {COMPANY.parent}
                                </dd>
                            </div>
                            <div className="flex justify-between gap-4">
                                <dt className="text-[#F9F6F0]/55">
                                    Adresse
                                </dt>
                                <dd className="text-right">
                                    Stubmarken 28<br />3230 Græsted
                                </dd>
                            </div>
                        </dl>
                    </div>

                    <div className="mt-8">
                        <div className="overline mb-4">Følg med</div>
                        <div className="flex flex-col gap-3">
                            <a
                                href={COMPANY.facebook}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-facebook-link"
                                className="inline-flex items-center justify-between border border-[#2C241B]/30 hover:border-[#2F483A] hover:text-[#2F483A] px-5 py-4 rounded-sm transition-colors"
                            >
                                <span className="inline-flex items-center gap-3 text-sm uppercase tracking-[0.18em]">
                                    <Facebook size={16} /> Facebook
                                </span>
                                <span className="text-xs text-[#7A6855]">
                                    @fyrogfiner
                                </span>
                            </a>
                            <a
                                href={COMPANY.instagram}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-instagram-link"
                                className="inline-flex items-center justify-between border border-[#2C241B]/30 hover:border-[#2F483A] hover:text-[#2F483A] px-5 py-4 rounded-sm transition-colors"
                            >
                                <span className="inline-flex items-center gap-3 text-sm uppercase tracking-[0.18em]">
                                    <Instagram size={16} /> Instagram
                                </span>
                                <span className="text-xs text-[#7A6855]">
                                    @fyrogfiner
                                </span>
                            </a>
                        </div>
                    </div>
                </aside>
            </section>
        </div>
    );
}
